import React from "react";
import { Line } from "react-konva";
import { EPDStyle, TRACK_LINE_WIDTH, WithLineStyle } from "../core/styles";
import {
  MovementAction,
  WithShuntingUnit,
  WithTracksAndGeometry,
} from "@opstel/query/v2/models/action";
import { useEPDStyle } from "../contexts/EPDStyle/useEPDStyle";
import { LineString } from "@opstel/geo/LineString";
import { LineSegment } from "@opstel/geo/LineSegment";
import { last } from "@shared/common/types/Array";
import { SCALING_VECTOR } from "../core/layoutConstants";

export type MovementActionWithTracksAndGeometry = Omit<MovementAction, "tracks"> &
  WithShuntingUnit &
  WithTracksAndGeometry;

interface TrainMovementProps {
  readonly action: MovementActionWithTracksAndGeometry;
}

function getCurrentStyle(
  style: EPDStyle,
  action: MovementActionWithTracksAndGeometry,
): WithLineStyle {
  // For now we don't do style changes with hovering over a train movement.
  const [_hoverActivatorStyle, normalStyle] =
    style.tracks[action.isNetworkMovement ? "networkMovement" : "shuntingMovement"];
  return normalStyle;
}

export function TrainMovement({ action }: TrainMovementProps): React.JSX.Element {
  const { style } = useEPDStyle();

  const currentStyle = getCurrentStyle(style, action);
  const movementPath = constructMovementPath(
    action.tracksWithGeometry.map(
      (t) =>
        new LineString(
          t.coordinates.fromSourceToTarget.map((coord) => coord.scale(SCALING_VECTOR)),
        ),
    ),
  );
  return (
    <>
      <Line
        x={0}
        y={0}
        points={movementPath.toOrdinates()}
        stroke={currentStyle.lineColor}
        strokeWidth={currentStyle.lineWidth * TRACK_LINE_WIDTH}
      />
      {movementPath.toSegments().map((segment) => (
        <MovementArrow
          key={`movement-arrow-${segment.start.x}-${segment.start.y}-${segment.end.x}-${segment.end.y}`}
          segment={segment}
          currentStyle={currentStyle}
        />
      ))}
    </>
  );
}
interface MovementArrowProps {
  readonly segment: LineSegment;
  readonly currentStyle: WithLineStyle;
}
function MovementArrow({ segment, currentStyle }: MovementArrowProps): React.JSX.Element | null {
  const desiredArrowLength = 9;
  const center = segment.center();
  const vectorX = segment.end.x - segment.start.x;
  const vectorY = segment.end.y - segment.start.y;
  const length = Math.sqrt(Math.pow(vectorX, 2) + Math.pow(vectorY, 2));
  const ratio = desiredArrowLength / (length === 0 ? 1 : length);
  const points = [0, 0, vectorX * ratio, vectorY * ratio];

  return (
    <>
      <Line
        x={center.x}
        y={center.y}
        points={points}
        stroke={currentStyle.lineColor}
        strokeWidth={currentStyle.lineWidth * TRACK_LINE_WIDTH}
        rotation={135}
      />
      <Line
        x={center.x}
        y={center.y}
        points={points}
        stroke={currentStyle.lineColor}
        strokeWidth={currentStyle.lineWidth * TRACK_LINE_WIDTH}
        rotation={-135}
      />
    </>
  );
}
function constructMovementPath(orderedLines: LineString[]): LineString {
  if (orderedLines.length == 1) return orderedLines[0];

  let path = orderedLines[0].toCoordinates();
  if (orderedLines[1].touches(path[0])) {
    path.reverse();
  }

  for (let i = 1; i < orderedLines.length; i++) {
    const lineToAdd = orderedLines[i].toCoordinates();
    if (!lineToAdd[0].equals(last(path))) {
      lineToAdd.reverse();
    }

    lineToAdd.shift();
    path = path.concat(lineToAdd);
  }

  return new LineString(path);
}
